import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { Injectable, inject } from "@angular/core";
import { Observable, catchError, map } from "rxjs";
import { HttpClient, HttpContext, HttpContextToken, HttpHeaders, HttpParams } from "@angular/common/http";

import { UserToken } from "../../utilities";
import { API_Response, GetPagedBody, GetPagedResponse, HttpStatus } from "../../interfaces";

import { environment } from "src/environments/environment";

export declare interface HttpServiceBaseCRUD<T = unknown, K = unknown> {
  add(body: Partial<K>): Observable<T>;
  update(body: Partial<K>): Observable<T>;
  remove(id: string): Observable<boolean>;
  getById(id: string): Observable<T>;
  getAll(): Observable<T[]>;
  getPaged(body: GetPagedBody<Partial<unknown>>): Observable<GetPagedResponse<T[]>>;
}

interface URL_Config {
  APIName: string;
  body?: unknown;
  showAlert?: boolean;

  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: "body";
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
  transferCache?:
    | {
        includeHeaders?: string[];
      }
    | boolean;
}

export const systemLoader = new HttpContextToken<string>(() => "loader");
export const loaderContext = (contextValue: string) => new HttpContext().set(systemLoader, contextValue);

@Injectable({
  providedIn: "root",
})
export abstract class HttpService {
  private http = inject(HttpClient);
  toastrService = inject(ToastrService);
  translation = inject(TranslateService);

  protected abstract get baseUrl(): string;

  get<T>(URL_Config: URL_Config) {
    return this.http.get<API_Response<T>>(`${this.baseUrl}${URL_Config.APIName}`, { ...URL_Config }).pipe(
      map((event) => {
        if (URL_Config.showAlert && event.statusCode === HttpStatus.Ok) this.alertHandling(event);

        if ("data" in event) return event.data;

        return event as T;
      }),
      catchError(() => []),
    );
  }

  getPagedRequest<T>(URL_Config: URL_Config) {
    return this.http.post<GetPagedResponse<T>>(`${this.baseUrl}${URL_Config.APIName}`, URL_Config.body, { ...URL_Config }).pipe(
      map((event) => {
        if (URL_Config.showAlert && [HttpStatus.Ok, HttpStatus.Created, HttpStatus.NoContent].includes(event.statusCode))
          this.alertHandling(event);

        if (!event.data && [HttpStatus.BadRequest, HttpStatus.NotFound].includes(event.statusCode)) {
          throw new Error(event.message);
        }

        return event;
      }),
    );
  }

  post<T>(URL_Config: URL_Config) {
    return this.http.post<API_Response<T>>(`${this.baseUrl}${URL_Config.APIName}`, URL_Config.body, { ...URL_Config }).pipe(
      map((event) => {
        if (URL_Config.showAlert && [HttpStatus.Ok, HttpStatus.Created, HttpStatus.NoContent].includes(event.statusCode))
          this.alertHandling(event);

        if (!event.data && [HttpStatus.BadRequest, HttpStatus.NotFound].includes(event.statusCode)) {
          throw new Error(event.message);
        }

        if ("data" in event) return event.data;

        return event as T;
      }),
    );
  }

  put<T>(URL_Config: URL_Config): Observable<T> {
    return this.http.put<API_Response<T>>(`${this.baseUrl}${URL_Config.APIName}`, URL_Config.body, { ...URL_Config }).pipe(
      map((event) => {
        if (URL_Config.showAlert && [HttpStatus.Ok, HttpStatus.Created, HttpStatus.NoContent].includes(event.statusCode))
          this.alertHandling(event);

        if (!event.data && [HttpStatus.BadRequest, HttpStatus.NotFound].includes(event.statusCode)) {
          throw new Error(event.message);
        }

        return event.data;
      }),
    );
  }

  delete<T = boolean>(URL_Config: URL_Config): Observable<T> {
    return this.http.delete<API_Response<T>>(`${this.baseUrl}${URL_Config.APIName}`, { ...URL_Config }).pipe(
      map((event) => {
        this.alertHandling(event);
        return event.data;
      }),
    );
  }

  private alertHandling(event: API_Response<unknown>) {
    if (event.statusCode) {
      if (event.statusCode.toString().startsWith("2")) {
        this.toastrService.success(event.message ? this.translation.instant(`BE_VALIDATION.${event.message}`) : "Successfully Done...");
      } else {
        this.toastrService.error(event.message ? this.translation.instant(`BE_VALIDATION.${event.message}`) : "!NOT HANDLED ERROR!");
      }
    }
  }

  convertTokenJWT(token = localStorage.getItem(environment.tokenPropertyName) as string) {
    if (token) {
      if (!token.split(".")[1]) return null;

      let base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"),
        jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );

      return JSON.parse(jsonPayload) as UserToken;
    }

    return null;
  }
}
